import * as React from 'react';
import { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import InboundMethods from './InboundMethods';

interface PricingOptionsProps {
  formData: any;
  optionsKey: string;
  disableForm?: boolean;
  handleToggleChange(event, value: any);
}

type FulfillmentWarningType = 'ecommerce' | 'retail' | null;

const ECOMMERCE_FULFILLMENT_WARNING_OFF = 'This will disable parcel fulfillment for this reservation. Are you sure?';
const RETAIL_FULFILLMENT_WARNING_OFF = 'This will disable freight Fulfillment for this reservation. Are you sure?';

const PricingOptions = ({ formData, handleToggleChange, optionsKey, disableForm }: PricingOptionsProps) => {
  const [fulfillmentWarningType, setFulfillmentWarningType] = useState<FulfillmentWarningType>(null);

  const handleEcommerceSwitchChange = (event: React.ChangeEvent<HTMLInputElement>, newValue: boolean) => {
    if (newValue) {
      handleToggleChange(event, true);
    } else {
      setFulfillmentWarningType('ecommerce');
    }
  };

  const handleRetailSwitchChange = (event: React.ChangeEvent<HTMLInputElement>, newValue: boolean) => {
    if (newValue) {
      handleToggleChange(event, true);
    } else {
      setFulfillmentWarningType('retail');
    }
  };

  const handleFulfillmentWarningConfirm = () => {
    if (fulfillmentWarningType === 'ecommerce') {
      const syntheticEvent = {
        target: { name: `${optionsKey}.consumer_fulfillment_pricing_scope` },
      } as React.ChangeEvent<HTMLInputElement>;
      handleToggleChange(syntheticEvent, false);
    } else if (fulfillmentWarningType === 'retail') {
      const syntheticEvent = {
        target: { name: `${optionsKey}.retail_fulfillment_pricing_scope` },
      } as React.ChangeEvent<HTMLInputElement>;
      handleToggleChange(syntheticEvent, false);
    }
    setFulfillmentWarningType(null);
  };

  const handleFulfillmentWarningCancel = () => {
    setFulfillmentWarningType(null);
  };

  const fulfillmentWarningTitle = 'WARNING';
  const fulfillmentWarningMessage =
    fulfillmentWarningType === 'ecommerce' ? ECOMMERCE_FULFILLMENT_WARNING_OFF : RETAIL_FULFILLMENT_WARNING_OFF;

  return (
    <div className="pricing-options">
      <fieldset>
        <legend>Pricing By</legend>
        <FormControlLabel
          control={
            <Switch
              name={`${optionsKey}.use_inventory_groups`}
              checked={formData.use_inventory_groups}
              onChange={handleToggleChange}
              disabled={disableForm}
            />
          }
          disabled={disableForm || formData.sort_center_pricing_scope}
          labelPlacement="end"
          label="Inventory Groups"
        />
        <FormControlLabel
          control={
            <Switch
              name={`${optionsKey}.use_packaging_groups`}
              checked={formData.use_packaging_groups}
              onChange={handleToggleChange}
            />
          }
          disabled={disableForm}
          labelPlacement="end"
          label="Packaging"
        />
      </fieldset>
      <InboundMethods
        formData={formData}
        optionsKey={optionsKey}
        handleToggleChange={handleToggleChange}
        disableForm={disableForm}
      />
      <fieldset>
        <React.Fragment>
          <legend>Outbound Method(s) - Select at least 1</legend>
          <div>
            <FormControlLabel
              control={
                <Switch
                  name={`${optionsKey}.consumer_fulfillment_pricing_scope`}
                  checked={formData.consumer_fulfillment_pricing_scope}
                  onChange={handleEcommerceSwitchChange}
                />
              }
              disabled={disableForm}
              labelPlacement="end"
              label="eCommerce Fulfillment"
            />
            <FormControlLabel
              control={
                <Switch
                  name={`${optionsKey}.retail_fulfillment_pricing_scope`}
                  checked={formData.retail_fulfillment_pricing_scope}
                  onChange={handleRetailSwitchChange}
                />
              }
              disabled={disableForm}
              labelPlacement="end"
              label="Retail Fulfillment"
            />
            <FormControlLabel
              control={
                <Switch
                  name={`${optionsKey}.sort_center_pricing_scope`}
                  checked={formData.sort_center_pricing_scope}
                  onChange={handleToggleChange}
                />
              }
              disabled={disableForm || formData.use_inventory_groups}
              labelPlacement="end"
              label="Sort Center"
            />
          </div>
        </React.Fragment>
      </fieldset>
      <fieldset>
        <legend>Additional Services</legend>
        <FormControlLabel
          control={
            <Switch
              name={`${optionsKey}.returns_pricing_scope`}
              checked={formData.returns_pricing_scope}
              onChange={handleToggleChange}
            />
          }
          disabled={disableForm}
          labelPlacement="end"
          label="Returns"
        />
      </fieldset>
      <Dialog open={fulfillmentWarningType !== null} onClose={handleFulfillmentWarningCancel}>
        <DialogTitle>{fulfillmentWarningTitle}</DialogTitle>
        <DialogContent>{fulfillmentWarningMessage}</DialogContent>
        <DialogActions>
          <Button onClick={handleFulfillmentWarningCancel}>Cancel</Button>
          <Button onClick={handleFulfillmentWarningConfirm} variant="contained" color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PricingOptions;
